import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faStar, faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';

import accessLocalStorage from '../../../services/accessLocalStorage';
import { getCsatSurvey, createCsatSurvey, updateCsatSurvey, getCsatAudios } from '../../../services/csatApi';
import Loading from '../../Loading/LoadingModule';
import {
  CSAT_MESSAGES,
  audioKey,
  buildCsatPayload,
  emptyCsatForm,
  formFromSurvey,
  resultErrorMessage,
} from './csatScreen';

import '../../../libs/bulma.min.css';
import '../Message/MessageList.css';

const readSession = () => accessLocalStorage.getUserLocalStorage();

const sessionTenant = () => {
  const session = readSession();
  return session && session.user ? session.user.idtenant : null;
};

function CsatSurveyForm() {
  const history = useHistory();
  const { id } = useParams();
  const isEdit = id !== undefined;

  const [values, setValues] = useState(emptyCsatForm());
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [warning, setWarning] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!readSession()) {
      history.push('/');
      return undefined;
    }
    let active = true;
    const tenant = sessionTenant();
    Promise.all([getCsatAudios(tenant), isEdit ? getCsatSurvey(id, tenant) : Promise.resolve(null)]).then(([audioResult, surveyResult]) => {
      if (!active) return;
      setAudios(audioResult.success ? audioResult.data : []);
      if (surveyResult) {
        if (surveyResult.success && surveyResult.data) setValues(formFromSurvey(surveyResult.data));
        else setError(resultErrorMessage(surveyResult, CSAT_MESSAGES.loadFailed));
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [history, id, isEdit]);

  const change = (field) => (event) => {
    const { type, checked, value } = event.target;
    setValues((prev) => ({ ...prev, [field]: type === 'checkbox' ? checked : value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    setWarning(null);
    const payload = buildCsatPayload(values, sessionTenant(), isEdit ? 'update' : 'create');
    const result = isEdit ? await updateCsatSurvey(id, payload) : await createCsatSurvey(payload);
    setSaving(false);
    if (!result.success) {
      setError(resultErrorMessage(result, CSAT_MESSAGES.saveFailed));
      return;
    }
    setSuccess(isEdit ? CSAT_MESSAGES.updated : CSAT_MESSAGES.created);
    if (result.data && result.data.dialplanReloaded === false) setWarning(CSAT_MESSAGES.reloadPending);
  };

  if (loading) return <Loading />;

  const audioOptions = audios.map((audio) => (
    <option key={audioKey(audio)} value={audioKey(audio)}>{`${audio.type} / ${audio.file}`}</option>
  ));

  return (
    <div className="message-list-container">
      {error && <div className="notification is-danger" role="alert">{error}</div>}
      {success && <div className="notification is-success">{success}</div>}
      {warning && <div className="notification is-warning" role="status">{warning}</div>}

      <div className="message-list-header">
        <div className="message-list-title-wrapper">
          <div className="message-list-title-left">
            <FontAwesomeIcon icon={faStar} className="message-list-icon" />
            <div>
              <h1 className="message-list-title">{isEdit ? CSAT_MESSAGES.editTitle : CSAT_MESSAGES.newTitle}</h1>
              <p className="message-list-subtitle">Áudio da pergunta, escala e tempo para o cliente digitar a nota</p>
            </div>
          </div>
          <div className="message-list-actions">
            <button type="button" className="btn-new-message" style={{ background: '#e5e7eb', color: '#2c3e50' }} onClick={() => history.push('/config/csat')}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Voltar
            </button>
          </div>
        </div>
      </div>

      <div className="columns is-centered">
        <div className="column is-8">
          <div className="box">
            <form onSubmit={submit}>
              <div className="field">
                <label className="label" htmlFor="csat-name">{CSAT_MESSAGES.nameLabel}</label>
                <div className="control">
                  <input id="csat-name" className="input" maxLength={100} value={values.name} onChange={change('name')} />
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="csat-slug">{CSAT_MESSAGES.slugLabel}</label>
                <div className="control">
                  <input id="csat-slug" className="input" maxLength={30} value={values.slug} onChange={change('slug')} readOnly={isEdit} />
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="csat-scale">{CSAT_MESSAGES.scaleLabel}</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select id="csat-scale" value={String(values.scale)} onChange={change('scale')}>
                      <option value="5">1 a 5</option>
                      <option value="10">1 a 10</option>
                    </select>
                  </div>
                </div>
                {Number(values.scale) === 10 && <p className="help">{CSAT_MESSAGES.scaleTenHint}</p>}
              </div>

              <div className="field">
                <label className="label" htmlFor="csat-question">{CSAT_MESSAGES.questionLabel}</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select id="csat-question" value={values.questionAudio} onChange={change('questionAudio')}>
                      <option value="">{CSAT_MESSAGES.chooseAudio}</option>
                      {audioOptions}
                    </select>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="label" htmlFor="csat-thanks">{CSAT_MESSAGES.thanksLabel}</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select id="csat-thanks" value={values.thanksAudio} onChange={change('thanksAudio')}>
                      <option value="">{CSAT_MESSAGES.noThanks}</option>
                      {audioOptions}
                    </select>
                  </div>
                </div>
              </div>

              <div className="columns">
                <div className="column">
                  <div className="field">
                    <label className="label" htmlFor="csat-attempts">{CSAT_MESSAGES.attemptsLabel}</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select id="csat-attempts" value={String(values.maxAttempts)} onChange={change('maxAttempts')}>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column">
                  <div className="field">
                    <label className="label" htmlFor="csat-timeout">{CSAT_MESSAGES.timeoutLabel}</label>
                    <div className="control">
                      <input id="csat-timeout" className="input" type="number" min={3} max={15} value={values.inputTimeoutSeconds} onChange={change('inputTimeoutSeconds')} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="checkbox" htmlFor="csat-active">
                  <input id="csat-active" type="checkbox" checked={values.active} onChange={change('active')} />
                  <span className="ml-2">
                    <FontAwesomeIcon icon={values.active ? faToggleOn : faToggleOff} className={`mr-1 ${values.active ? 'has-text-success' : 'has-text-grey'}`} />
                    {CSAT_MESSAGES.activeLabel}
                  </span>
                </label>
              </div>

              <div className="field is-grouped is-grouped-right">
                <div className="control">
                  <button type="button" className="button is-light" onClick={() => history.push('/config/csat')}>
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    {CSAT_MESSAGES.cancelButton}
                  </button>
                </div>
                <div className="control">
                  <button type="submit" className={`button is-primary ${saving ? 'is-loading' : ''}`} disabled={saving}>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    {CSAT_MESSAGES.saveButton}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CsatSurveyForm;
